import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Eye, EyeOff, Lock, Mail, AlertCircle, TreePine } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';

const LoginPage = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const resolveRedirect = () => {
    const param = searchParams.get('redirect');
    if (param && param.startsWith('/') && !param.startsWith('//')) return param;
    const pendingCode = localStorage.getItem('pendingTaskCode');
    if (pendingCode) return `/task/${pendingCode}`;
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const user = await login(email, password);
      toast.success(t('portalUi.login.successToast', 'Veiksmīgi pieslēdzāties!'));
      
      const redirectTo = resolveRedirect();
      if (redirectTo) {
        navigate(redirectTo, { replace: true });
      } else if (user.role === 'admin') {
        navigate('/portal/admin');
      } else if (user.role === 'employee') {
        navigate('/portal/employee');
      } else {
        navigate('/portal/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('portalUi.login.errorFallback', 'Pieslēgšanās kļūda'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8faf8] to-white flex flex-col items-center justify-center p-4" data-testid="portal-login">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-64 h-64 bg-[#1F3D2B]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-[#1F3D2B]/5 rounded-full blur-3xl" />
      </div>

      {/* Logo */}
      <div className="relative z-10 mb-8 text-center">
        <div className="flex items-center justify-center gap-3 mb-2">
          <img
            src={companyInfo.logo.dark}
            alt={companyInfo.name}
            className="h-12 w-auto"
          />
          <div>
            <h1 className="text-2xl font-bold text-[#1F3D2B]">{companyInfo.name}</h1>
            <p className="text-sm text-[#2E2E2E]/60" data-testid="portal-login-label">{t('portalUi.login.portalLabel', 'Klientu portāls')}</p>
          </div>
        </div>
      </div>

      {/* Login Card */}
      <Card className="relative z-10 w-full max-w-md shadow-xl border-0" data-testid="portal-login-card">
        <CardHeader className="text-center pb-4">
          <div className="w-16 h-16 rounded-full bg-[#1F3D2B]/10 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-[#1F3D2B]" />
          </div>
          <CardTitle className="text-2xl text-[#2E2E2E]" data-testid="portal-login-title">
            {t('portalUi.login.title', 'Pieslēgties klientu portālam')}
          </CardTitle>
          <CardDescription className="text-[#2E2E2E]/60" data-testid="portal-login-description">
            {t('portalUi.login.description', 'Šeit varat sekot līdzi darbu statusiem, dokumentiem un atskaitēm par Jūsu īpašumu.')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="portal-login-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">{t('portalUi.login.emailLabel', 'E-pasts')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#2E2E2E]/40" />
                <Input
                  id="email" data-testid="portal-login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('portalUi.login.emailPlaceholder', 'Ievadiet e-pastu')}
                  className="pl-10 h-12"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('portalUi.login.passwordLabel', 'Parole')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#2E2E2E]/40" />
                <Input
                  id="password" data-testid="portal-login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('portalUi.login.passwordPlaceholder', 'Ievadiet paroli')}
                  className="pl-10 pr-10 h-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="portal-login-password-toggle"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2E2E2E]/40 hover:text-[#2E2E2E]/60 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                <span className="sr-only">{t('portalUi.login.togglePassword', 'Rādīt paroli')}</span>
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-[#1F3D2B] hover:bg-[#2a5039] text-white font-semibold"
              disabled={isLoading}
              data-testid="portal-login-submit"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t('portalUi.login.submitting', 'Pieslēdzas...')}
                </span>
              ) : (
                t('portalUi.login.submit', 'Pieslēgties')
              )}
            </Button>

            <div className="text-center">
              <p className="text-sm text-[#2E2E2E]/60">
                {t('portalUi.login.forgotText', 'Aizmirsu paroli?')}{' '}
                <a href="/portal/forgot-password" className="text-[#1F3D2B] hover:underline font-medium" data-testid="portal-login-forgot-link">
                  {t('portalUi.login.resetLink', 'Atjaunot paroli')}
                </a>
              </p>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Security notice */}
      <div className="relative z-10 mt-6 max-w-md text-center">
        <div className="p-4 bg-white/80 backdrop-blur-sm rounded-xl border border-gray-100">
          <div className="flex items-center justify-center gap-2 text-[#1F3D2B] mb-2">
            <Lock className="w-4 h-4" />
            <span className="font-medium text-sm">{t('portalUi.login.securityTitle', 'Drošības paziņojums')}</span>
          </div>
          <p className="text-xs text-[#2E2E2E]/60">
            {t('portalUi.login.securityText', 'Piekļuve tikai autorizētiem klientiem. Ja nepieciešama piekļuve, sazinieties ar mums:')}
          </p>
          <p className="text-sm text-[#1F3D2B] font-medium mt-1">
            {companyInfo.phone} / {companyInfo.email}
          </p>
        </div>
      </div>

      {/* Back to main site */}
      <a
        href="/"
        className="relative z-10 mt-6 text-sm text-[#2E2E2E]/60 hover:text-[#1F3D2B] transition-colors flex items-center gap-2"
        data-testid="portal-login-back-link"
      >
        <TreePine className="w-4 h-4" />
        {t('portalUi.login.backToSite', 'Atpakaļ uz galveno lapu')}
      </a>
    </div>
  );
};

export default LoginPage;
